// AdminPanel.js — Interface d'administration complète

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { events as localEvents, CATEGORIES, formatYear } from '../data/events';

const ADMIN_EMAIL = 'charl.delattre@gmail.com'; // seul cet email a accès

// ── Steps tutoriel par défaut ────────────────────────────────────────────────
const DEFAULT_TUTO_STEPS = [
  {
    icon: "🌍",
    title: "Bienvenue sur\nKronotera",
    subtitle: "Pret a voyager dans le temps ?",
    desc: "Kronotera vous permet d'explorer l'Histoire mondiale sur un globe 3D. Parcourez 5000 ans d'evenements historiques geolocali ses.",
    tip: "Faites pivoter le globe en cliquant-glissant, zoomez avec la molette.",
  },
  {
    icon: "⏱️",
    title: "La timeline\nhistorique",
    subtitle: "Naviguez dans 5500 ans d'histoire",
    desc: "Le curseur en bas est votre machine a voyager dans le temps. Glissez-le pour choisir une annee entre -3500 et aujourd'hui.",
    tip: "Les boutons Even. prec. et Even. suiv. sautent d'evenement en evenement.",
  },
  {
    icon: "📍",
    title: "Explorez\nles evenements",
    subtitle: "Chaque point raconte une histoire",
    desc: "Sur le globe, chaque point lumineux represente un evenement historique geolocal ise. La couleur indique sa categorie.",
    tip: "Cliquez sur un point pour ouvrir sa fiche detaillee.",
  },
  {
    icon: "🔍",
    title: "Recherche\net filtres",
    subtitle: "Trouvez n'importe quel evenement",
    desc: "La barre de recherche (Ctrl+K) vous permet de trouver instantanement n'importe quel evenement par son nom ou son annee.",
    tip: "Activez le bouton 🔗 pour voir les connexions entre evenements.",
  },
  {
    icon: "✚",
    title: "Contribuez\na l'Histoire",
    subtitle: "Devenez auteur de l'Histoire mondiale",
    desc: "Kronotera est une plateforme collaborative. Creez un compte et soumettez vos contributions. Chaque evenement est verifie avant d'apparaitre.",
    tip: "Titre, date, coordonnees, description. Simple et rapide.",
  },
];

// ── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'events',    label: '📝 Fiches événements' },
  { id: 'community', label: '👥 Soumissions communauté' },
  { id: 'messages',  label: '✉️ Messages', badge: true },
  { id: 'stats',     label: '📊 Stats' },
];

// ── Éditeur de fiche ─────────────────────────────────────────────────────────
function EventEditor({ event, onSave, onClose }) {
  const [form, setForm] = useState({
    resume:       event.resume       || '',
    contexte:     event.contexte     || '',
    consequences: event.consequences || '',
    anecdote:     event.anecdote     || '',
    image_url:    event.image_url    || '',
    wiki_url:     event.wiki_url     || '',
    video_url:    event.video_url    || '',
    video_embed_url: event.video_embed_url || '',
    video_title:  event.video_title  || '',
    video_channel: event.video_channel || '',
    personnages:  (event.personnages || []).join(', '),
    mots_cles:    (event.mots_cles   || []).join(', '),
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      ...form,
      personnages: form.personnages.split(',').map(s => s.trim()).filter(Boolean),
      mots_cles:   form.mots_cles.split(',').map(s => s.trim()).filter(Boolean),
    };
    await onSave(event.id, payload);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const Field = ({ label, k, multiline = false, hint = '' }) => (
    <div className="admin-field">
      <label className="admin-field-label">{label}</label>
      {hint && <span className="admin-field-hint">{hint}</span>}
      {multiline ? (
        <textarea
          className="admin-field-input admin-textarea"
          value={form[k]}
          onChange={e => set(k, e.target.value)}
          rows={4}
        />
      ) : (
        <input
          className="admin-field-input"
          type="text"
          value={form[k]}
          onChange={e => set(k, e.target.value)}
        />
      )}
    </div>
  );

  return (
    <div className="admin-editor">
      <div className="admin-editor-header">
        <div>
          <h2 className="admin-editor-title">{event.title}</h2>
          <span className="admin-editor-year">{formatYear(event.year)}</span>
        </div>
        <button className="admin-close-btn" onClick={onClose}>×</button>
      </div>

      <div className="admin-editor-body">
        <div className="admin-section-title">📖 Contenu</div>
        <Field label="Résumé" k="resume" multiline hint="5-6 phrases vivantes" />
        <Field label="Contexte historique" k="contexte" multiline hint="3-4 phrases sur les causes" />
        <Field label="Conséquences" k="consequences" multiline hint="3-4 phrases sur l'impact" />
        <Field label="Anecdote" k="anecdote" multiline hint="2-3 phrases surprenantes" />
        <Field label="Personnages clés" k="personnages" hint="Séparés par des virgules" />
        <Field label="Mots-clés" k="mots_cles" hint="Séparés par des virgules" />

        <div className="admin-section-title" style={{ marginTop: 20 }}>🖼 Médias</div>
        <Field label="URL image" k="image_url" hint="URL directe vers une image" />
        <Field label="URL Wikipedia" k="wiki_url" hint="Lien vers l'article Wikipedia" />
        <Field label="URL YouTube" k="video_url" hint="https://www.youtube.com/watch?v=..." />
        <Field label="URL embed YouTube" k="video_embed_url" hint="https://www.youtube.com/embed/..." />
        <Field label="Titre vidéo" k="video_title" />
        <Field label="Chaîne YouTube" k="video_channel" />

        {/* Prévisualisation image */}
        {form.image_url && (
          <div className="admin-preview">
            <div className="admin-section-title">👁 Aperçu image</div>
            <img src={form.image_url} alt="" className="admin-preview-img"
              onError={e => e.target.style.display = 'none'} />
          </div>
        )}
      </div>

      <div className="admin-editor-footer">
        <button className="admin-cancel-btn" onClick={onClose}>Annuler</button>
        <button
          className={`admin-save-btn ${saved ? 'saved' : ''}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '⏳ Sauvegarde…' : saved ? '✓ Sauvegardé !' : '💾 Sauvegarder'}
        </button>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export function AdminPanel({ user, onClose, initialEvent }) {
  const [tab,           setTab]           = useState('events');
  const [enrichedMap,   setEnrichedMap]   = useState({});
  const [community,     setCommunity]     = useState([]);
  const [editingEvent,  setEditingEvent]  = useState(initialEvent || null);
  const [search,        setSearch]        = useState('');
  const [loading,       setLoading]       = useState(true);
  const [stats,         setStats]         = useState({});
  const [messages,      setMessages]      = useState([]);
  const [tutoSteps,     setTutoSteps]     = useState(null); // null = pas encore chargé
  const [editingStep,   setEditingStep]   = useState(null); // index du step en cours d'édition
  const [tutoSaved,     setTutoSaved]     = useState(false);

  // Vérif accès admin
  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    if (!isAdmin) return;
    loadData();
  }, [isAdmin]);

  async function loadData() {
    setLoading(true);
    // Charger toutes les fiches enrichies
    const { data: enriched } = await supabase.from('events_enriched').select('*');
    const map = {};
    (enriched || []).forEach(e => { map[e.id] = e; });
    setEnrichedMap(map);

    // Charger soumissions communauté
    const { data: comm } = await supabase
      .from('events_users')
      .select('*')
      .order('created_at', { ascending: false });
    setCommunity(comm || []);

    // Messages contact
    const { data: msgs } = await supabase
      .from('contacts')
      .select('*')
      .order('created_at', { ascending: false });
    setMessages(msgs || []);

    // Stats
    setStats({
      totalLocal:   localEvents.length,
      totalEnriched: (enriched || []).length,
      totalWithImg:  (enriched || []).filter(e => e.image_url).length,
      totalWithVideo:(enriched || []).filter(e => e.video_url).length,
      totalComm:     (comm || []).length,
      totalMessages: 0,
      pendingComm:   (comm || []).filter(e => e.status === 'pending').length,
    });

    // Charger steps du tuto depuis Supabase (table settings)
    try {
      const { data: tData } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'tutorial_steps')
        .single();
      setTutoSteps(tData?.value || DEFAULT_TUTO_STEPS);
    } catch {
      setTutoSteps(DEFAULT_TUTO_STEPS);
    }

    setLoading(false);
  }

  const handleSave = async (id, payload) => {
    const { error } = await supabase
      .from('events_enriched')
      .upsert({ id, ...payload });
    if (!error) {
      setEnrichedMap(m => ({ ...m, [id]: { ...m[id], ...payload } }));
    }
  };

  const handleCommunityStatus = async (id, status) => {
    await supabase.from('events_users').update({ status }).eq('id', id);
    setCommunity(c => c.map(e => e.id === id ? { ...e, status } : e));
  };

  const handleDeleteCommunity = async (id) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    await supabase.from('events_users').delete().eq('id', id);
    setCommunity(c => c.filter(e => e.id !== id));
  };

  if (!isAdmin) {
    return (
      <div className="admin-overlay">
        <div className="admin-panel">
          <div className="admin-access-denied">
            <span style={{ fontSize: 48 }}>🔒</span>
            <p>Accès réservé à l'administrateur</p>
            <button className="admin-cancel-btn" onClick={onClose}>Fermer</button>
          </div>
        </div>
      </div>
    );
  }

  const filteredEvents = localEvents.filter(e =>
    e.title.toLowerCase().includes(search.toLowerCase()) ||
    String(e.year).includes(search)
  );

  return (
    <div className="admin-overlay">
      <div className="admin-panel">

        {/* Header */}
        <div className="admin-header">
          <div className="admin-header-left">
            <span style={{ fontSize: 20 }}>⚙️</span>
            <h1 className="admin-title">Administration Kronotera</h1>
          </div>
          <button className="admin-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="admin-tabs">
          {TABS.map(t => (
            <button key={t.id}
              className={`admin-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}>
              {t.label}
              {t.id === 'community' && stats.pendingComm > 0 && (
                <span className="admin-badge">{stats.pendingComm}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="admin-loading">Chargement…</div>
        ) : (

          <div className="admin-content">

            {/* ── Fiches événements ── */}
            {tab === 'events' && (
              <div className="admin-events">
                {editingEvent ? (
                  <EventEditor
                    event={{ ...editingEvent, ...enrichedMap[editingEvent.id] }}
                    onSave={handleSave}
                    onClose={() => setEditingEvent(null)}
                  />
                ) : (
                  <>
                    <input
                      className="admin-search"
                      placeholder="🔍 Rechercher un événement…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                    <div className="admin-event-list">
                      {filteredEvents.map(ev => {
                        const cat      = CATEGORIES[ev.category];
                        const enriched = enrichedMap[ev.id];
                        return (
                          <button key={ev.id}
                            className="admin-event-row"
                            onClick={() => setEditingEvent(ev)}>
                            <span className="admin-event-icon">{cat.icon}</span>
                            <div className="admin-event-info">
                              <span className="admin-event-title">{ev.title}</span>
                              <span className="admin-event-year" style={{ color: cat.color }}>
                                {formatYear(ev.year)}
                              </span>
                            </div>
                            <div className="admin-event-status">
                              {enriched?.image_url   && <span title="Image">🖼</span>}
                              {enriched?.wiki_url    && <span title="Wikipedia">W</span>}
                              {enriched?.video_url   && <span title="Vidéo">🎬</span>}
                              {enriched?.resume      && <span title="Résumé">📝</span>}
                            </div>
                            <span className="admin-event-arrow">✏️</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Communauté ── */}
            {tab === 'community' && (
              <div className="admin-community">
                {community.length === 0 ? (
                  <p className="admin-empty">Aucune soumission.</p>
                ) : community.map(ev => (
                  <div key={ev.id} className={`admin-comm-row status-${ev.status}`}>
                    <div className="admin-comm-info">
                      <span className="admin-event-title">{ev.title}</span>
                      <span className="admin-event-year">{formatYear(ev.year)}</span>
                      <span className="admin-comm-email">{ev.user_email}</span>
                      {ev.description && <p className="admin-comm-desc">{ev.description}</p>}
                    </div>
                    <div className="admin-comm-actions">
                      <span className={`admin-status-badge status-${ev.status}`}>
                        {ev.status === 'pending' ? '⏳ En attente'
                         : ev.status === 'approved' ? '✅ Approuvé'
                         : '❌ Refusé'}
                      </span>
                      {ev.status !== 'approved' && (
                        <button className="admin-approve-btn"
                          onClick={() => handleCommunityStatus(ev.id, 'approved')}>
                          ✅ Approuver
                        </button>
                      )}
                      {ev.status !== 'rejected' && (
                        <button className="admin-reject-btn"
                          onClick={() => handleCommunityStatus(ev.id, 'rejected')}>
                          ❌ Refuser
                        </button>
                      )}
                      <button className="admin-delete-btn"
                        onClick={() => handleDeleteCommunity(ev.id)}>
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Messages ── */}
            {tab === 'messages' && (
              <div className="admin-community">
                {messages.length === 0 ? (
                  <p className="admin-empty">Aucun message reçu.</p>
                ) : messages.map(msg => (
                  <div key={msg.id} className={`admin-comm-row ${msg.type === 'report' ? 'status-rejected' : ''}`}>
                    <div className="admin-comm-info">
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span className="admin-msg-type">
                          {msg.type === 'report' ? '🚩 Signalement'
                           : msg.type === 'bug' ? '🐛 Bug'
                           : msg.type === 'suggestion' ? '💡 Suggestion'
                           : msg.type === 'partnership' ? '🤝 Partenariat'
                           : '💬 Contact'}
                        </span>
                        <span className="admin-comm-email">{msg.email}</span>
                        {msg.name && <span className="admin-comm-email">· {msg.name}</span>}
                      </div>
                      {msg.subject && <span className="admin-event-title">{msg.subject}</span>}
                      {msg.event_title && (
                        <span className="admin-comm-email">📍 {msg.event_title}</span>
                      )}
                      <p className="admin-comm-desc">{msg.message}</p>
                      <span className="admin-comm-email">
                        {new Date(msg.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="admin-comm-actions">
                      <a
                        href={`mailto:${msg.email}?subject=Re: ${encodeURIComponent(msg.subject || 'Kronotera')}`}
                        className="admin-approve-btn"
                        style={{ textDecoration: 'none', fontSize: 11 }}
                      >
                        ↩ Répondre
                      </a>
                      <button className="admin-delete-btn" onClick={async () => {
                        await supabase.from('contacts').delete().eq('id', msg.id);
                        setMessages(m => m.filter(x => x.id !== msg.id));
                      }}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Tutoriel ── */}
            {tab === 'tutorial' && (
              <div className="admin-events">
                {editingStep !== null ? (
                  // Éditeur de step
                  <div className="admin-editor">
                    <div className="admin-editor-header">
                      <div>
                        <h2 className="admin-editor-title">
                          {(tutoSteps || DEFAULT_TUTO_STEPS)[editingStep]?.icon} Étape {editingStep + 1}
                        </h2>
                      </div>
                      <button className="admin-close-btn" onClick={() => setEditingStep(null)}>×</button>
                    </div>
                    <div className="admin-editor-body">
                      {['icon','title','subtitle','desc','tip'].map(field => (
                        <div key={field} className="admin-field">
                          <label className="admin-field-label">{field}</label>
                          <textarea
                            className="admin-field-input admin-textarea"
                            rows={field === 'desc' ? 4 : 2}
                            value={(tutoSteps || DEFAULT_TUTO_STEPS)[editingStep]?.[field] || ''}
                            onChange={e => {
                              const updated = [...(tutoSteps || DEFAULT_TUTO_STEPS)];
                              updated[editingStep] = { ...updated[editingStep], [field]: e.target.value };
                              setTutoSteps(updated);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="admin-editor-footer">
                      <button className="admin-cancel-btn" onClick={() => setEditingStep(null)}>Retour</button>
                      <button
                        className={`admin-save-btn ${tutoSaved ? 'saved' : ''}`}
                        onClick={async () => {
                          await supabase.from('settings').upsert({ key: 'tutorial_steps', value: tutoSteps });
                          setTutoSaved(true);
                          setTimeout(() => setTutoSaved(false), 2000);
                        }}
                      >
                        {tutoSaved ? '✓ Sauvegardé !' : '💾 Sauvegarder'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ padding: '12px 16px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      Cliquez sur une étape pour la modifier. Les changements sont sauvegardés dans Supabase.
                    </div>
                    <div className="admin-event-list">
                      {(tutoSteps || DEFAULT_TUTO_STEPS).map((step, i) => (
                        <button key={i} className="admin-event-row" onClick={() => setEditingStep(i)}>
                          <span className="admin-event-icon">{step.icon}</span>
                          <div className="admin-event-info">
                            <span className="admin-event-title">{step.title?.replace('\n', ' ')}</span>
                            <span className="admin-event-year" style={{ color: 'var(--accent)' }}>{step.subtitle}</span>
                          </div>
                          <span className="admin-event-arrow">✏️</span>
                        </button>
                      ))}
                    </div>
                    <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
                      <button className="admin-cancel-btn" onClick={() => { setTutoSteps(DEFAULT_TUTO_STEPS); }}>
                        ↩ Réinitialiser par défaut
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Stats ── */}}}
            {tab === 'stats' && (
              <div className="admin-stats">
                {[
                  { label: 'Événements locaux',  value: stats.totalLocal,    icon: '📍' },
                  { label: 'Fiches enrichies',   value: stats.totalEnriched, icon: '📝' },
                  { label: 'Avec image',          value: stats.totalWithImg,  icon: '🖼' },
                  { label: 'Avec vidéo',          value: stats.totalWithVideo,icon: '🎬' },
                  { label: 'Soumissions',         value: stats.totalComm,     icon: '👥' },
                  { label: 'En attente',          value: stats.pendingComm,   icon: '⏳' },
                ].map(s => (
                  <div key={s.label} className="admin-stat-card">
                    <span className="admin-stat-icon">{s.icon}</span>
                    <span className="admin-stat-value">{s.value}</span>
                    <span className="admin-stat-label">{s.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
